import type { UTCDate } from '@date-fns/utc';
import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { CenterLoading } from '@revelio/core';
import {
  type CustomRoleTaxonomySelection,
  TaxonomyType,
} from '@revelio/data-access';
import { MAX_PRIMARY_ENTITY_SELECTIONS } from '@revelio/iso-utility';

import { RICS_TAXONOMY_ID } from '../../../../shared/selection-lists/constants';
import { useActivityTaxonomyTree } from '../../../../shared/selection-lists/hooks/useActivityTaxonomyTree';
import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useGeographyTaxonomyTree } from '../../../../shared/selection-lists/hooks/useGeographyTaxonomyTree';
import { useCurrentReferenceTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useReferenceTaxonomyVersions';
import { useCurrentRoleTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { useSelectionListsForKeys } from '../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { useTaxonomyTree } from '../../../../shared/selection-lists/hooks/useTaxonomyTree';
import {
  ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS,
  type AllSelectionListsWithSkillTaxonomyKey,
  type GqlSelectionListKey,
  gqlSelectionListKeys,
  type RoleSelectionListKey,
  type SelectionListKey,
  SKILL_TAXONOMY_SELECTION_LIST_KEYS,
  standardGqlSelectionListKeys,
} from '../../../../shared/selection-lists/types';
import { getRoleTaxonomyId } from '../../../../shared/selection-lists/utils/roleTaxonomy';
import {
  type ActivityFilterFlags,
  isActivityFilterOnForPrimary,
  useActivityFilterFlags,
} from '../../../../shared/unleash-flags/use-activity-filter-flags';
import { useUnleashReady } from '../../../../shared/unleash-flags/use-unleash-ready';
import {
  PreferenceScope,
  useUserPreferences,
} from '../../../../shared/user-preferences';
import type { RevelioPageId } from '../../components/PageConfigProvider/types';
import { useToast } from '../../hooks/useToast';
import type { CustomActivityTaxonomySelection } from '../definitions/activityTaxonomy';
import type { CustomGeographyTaxonomySelection } from '../definitions/geographyTaxonomy';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  type AdaptiveTaxonomyContext,
  deriveAdaptiveTaxonomies,
  enforceActivityFilterMembership,
} from '../engine/deriveAdaptiveTaxonomies';
import {
  createInitialFilterConsistencyState,
  createNewFilterState,
  type FilterConsistencyState,
  getActiveFilterMap,
  getPrimaryFilterIdFromConsistencyKey,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import { type FilterAction, filterReducer } from '../engine/filterReducer';
import {
  getFilterDefinition,
  getFilterHandler,
} from '../engine/filterRegistry';
import { useActivityDefaultsStaleNotice } from '../hooks/useActivityDefaultsStaleNotice';
import { useAllSelectionLists } from '../hooks/useAllSelectionLists';
import { useDeserializeFilterFromUrl } from '../hooks/useDeserializeFilterFromUrl';
import { useGeographyTaxonomy } from '../hooks/useGeographyTaxonomy';
import {
  applyGeographyTaxonomyToFilterMap,
  useGeographyTaxonomyChangeEffect,
} from '../hooks/useGeographyTaxonomySync';
import { useRoleDefaultsStaleNotice } from '../hooks/useRoleDefaultsStaleNotice';
import { useSkillDefaultsStaleNotice } from '../hooks/useSkillDefaultsStaleNotice';
import { useSyncFiltersToUrl } from '../hooks/useSyncFiltersToUrl';
import { sortCitySelectionList } from '../selectionList/sortCitySelectionList';
import {
  type AllSelectionListKeys,
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type PrimaryFilterId,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import { flattenTreeToSelectionLists } from '../utils/flattenTreeToSelectionLists';
import { COUNTY_EXCLUDED_PAGE_IDS } from '../utils/geography-county-capability';
import { applyResolvedSelectionLabelsToFilterMap } from '../utils/patchUnresolvedSelectionNames';
import {
  isRicsCompanyFilter,
  pruneTreeToDepth,
  RICS_COMPANY_SELECTION_LIST_IDS,
  RICS_INDUSTRY_SELECTION_LIST_IDS,
  RICS_TAXONOMY_TREE_LEVELS,
  stripTreeNodeTooltipData,
} from '../utils/ricsTaxonomyTree';
import { selectionListCache } from '../utils/selectionListCache';
import type { CustomSkillTreeNode } from '../utils/skillTaxonomyTree';
import {
  ROLE_TAXONOMY_SELECTION_LIST_IDS,
  ROLE_TAXONOMY_TREE_LEVELS,
} from '../utils/taxonomy-tree-levels';
import { toNestedTaxonomyTree } from '../utils/taxonomyNestedTree';
import type { DefaultSelectionOverride } from './defaultFilters';
import {
  getSharedFilterState,
  setSharedFilterState,
} from './sharedFilterState';
import { useSyncMetricModePostingSource } from './useSyncMetricModePostingSource';

interface DateRangeBounds {
  min: UTCDate;
  max: UTCDate;
}

export type DateGranularity = 'week' | 'month';

export type WeekMonthDateRanges = {
  [key in DateGranularity]: DateRangeBounds;
};

export interface TreeData {
  id: string;
  name: string;
  children?: TreeData[];
  depth?: number;
  levelId?: AllSelectionListsWithSkillTaxonomyKey;
  nodeCount?: number;
  info?: string;
  suggestedTitles?: string[];
  taxonomyTitles?: string | readonly (string | null)[] | null;
}

export interface ListData {
  selectionListId: SelectionListKey;
  selectionList: { id: string; shortName: string; longName: string }[];
}

export type FilterDataResult = TreeData[] | ListData[];

const ROLE_TAXONOMY_SELECTION_LIST_ID_SET = new Set<string>(
  ROLE_TAXONOMY_SELECTION_LIST_IDS
);

const SKILL_TAXONOMY_SELECTION_LIST_ID_SET = new Set<string>(
  SKILL_TAXONOMY_SELECTION_LIST_KEYS
);

const ACTIVITY_TAXONOMY_SELECTION_LIST_ID_SET = new Set<string>(
  ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS
);

interface FilterContextType {
  filterState: FilterConsistencyState;
  activeFilterMap: FilterSelectionsMap;
  dispatch: React.Dispatch<FilterAction>;
  getFilterData: (filterId: FilterId) => FilterDataResult;
  hadInitialUrlFilters: boolean;
  defaultSelectionOverrides?: DefaultSelectionOverride;
  /** True once adaptive taxonomy hooks have resolved (always true for non-company pages). */
  isTaxonomyResolved: boolean;
  /**
   * True while a reference tree backing a displayed filter is still fetching, so
   * tree/list selection names may still be raw ids awaiting label resolution.
   * Distinct from `isTaxonomyResolved` (the taxonomy VALUE): the label tree is
   * fetched AFTER the value settles, so chips can render a placeholder until the
   * real name lands instead of flashing the numeric id.
   */
  isResolvingSelectionLabels: boolean;
  /** Current geography granularity — exposed so consumers can react to mode changes. */
  usesStateBreakdownOverMsa: boolean;
  /**
   * The active geography taxonomy's level keys, shallowest first. Consumers
   * that need the actual levels (depth lookups, breakdown EntKinds) must read
   * this rather than inferring them from `usesStateBreakdownOverMsa`, which cannot express
   * a taxonomy's fourth level.
   */
  geographySelectionListIds: readonly string[];
  /** Active geography taxonomy id + version, for consumers that fetch the tree themselves. */
  geographyTaxonomyId: number;
  geographyTaxonomyVersion: string | undefined;
  /** Page-resolved gate, including Unleash readiness; shared by all filter consumers. */
  activityTaxonomyEnabled: boolean;
  /** Current activity taxonomy version; undefined while the gate is off. */
  activityTaxonomyVersion: string | undefined;
  /** The skill taxonomy the page's filters pin, for consumers that fetch the tree themselves. */
  skillTaxonomyId: number | undefined;
  skillTaxonomyVersion: string | undefined;
  /** The activity taxonomy the page's filters pin; validation keeps its version at `activityTaxonomyVersion`. */
  activityTaxonomyId: number | undefined;
}

const FilterContext = createContext<FilterContextType | null>(null);

type FilterProviderProps = PropsWithChildren & {
  revelioPageId?: RevelioPageId;
  primaryFilterId?: PrimaryFilterId;
  defaultSelectionOverrides?: DefaultSelectionOverride;
  /**
   * When false, initial state ignores URL `filters` / `filterGroups` (isolated instances, e.g. AI chart preview).
   * Defaults true for normal dashboard routes.
   */
  hydrateFromUrl?: boolean;
  /**
   * When false, filter changes are not written to the URL.
   * Defaults true for normal dashboard routes.
   */
  syncFiltersToUrl?: boolean;
  /**
   * When false, this provider's state is isolated — it does not hydrate from
   * the in-memory shared filter state and does not write back to it.
   * Required for mounts whose state must stay separate (Report Builder, AI
   * chart preview). Defaults true for normal dashboard routes.
   */
  persistScope?: boolean;
};

export const FilterProvider = (props: FilterProviderProps) => {
  const {
    version: roleTaxonomyVersion,
    loading: isRoleTaxonomyVersionLoading,
  } = useCurrentRoleTaxonomyVersion();

  const {
    version: skillTaxonomyVersion,
    getSpecificTaxonomyVersion: getSpecificSkillTaxonomyVersion,
    loading: isSkillTaxonomyVersionLoading,
  } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Skill,
  });

  const isUnleashReady = useUnleashReady();
  // Live, never latched: `useUnleashReady` reports ready on `flagsReady ||
  // flagsError`, which can precede this flag's real value by a render (or a
  // whole retry after a transient unleash error) — a latch captured at that
  // moment freezes `false` and hides the feature for the session. Late flips
  // are instead absorbed below: the loading gate never re-blocks after first
  // mount, and the reducer enforces map membership from the live flag.
  const activityFilterFlags = useActivityFilterFlags();
  // Custom pages can change primary without remounting. Follow the live group
  // for taxonomy loading; the route prop seeds the gate before state exists.
  const [activePrimaryFilterId, setActivePrimaryFilterId] = useState<
    PrimaryFilterId | undefined
  >(props.primaryFilterId);
  useEffect(() => {
    setActivePrimaryFilterId(props.primaryFilterId);
  }, [props.primaryFilterId]);
  const isActivityTaxonomyEnabled =
    isActivityFilterOnForPrimary(activityFilterFlags, activePrimaryFilterId) &&
    isUnleashReady;

  const {
    version: activityTaxonomyVersion,
    loading: isActivityTaxonomyVersionLoading,
  } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Activity,
    pause: !isActivityTaxonomyEnabled,
  });

  const { isInitialLoading: isPreferencesLoading } = useUserPreferences(
    PreferenceScope.GLOBAL
  );

  // One-way gate: once the stateful provider below has mounted, a flag flip
  // that re-pauses/re-fires the activity version query must not swap it back
  // for <CenterLoading /> — that would unmount the provider and wipe the
  // user's in-progress filter state.
  const hasMountedChildRef = useRef(false);
  const isBlockingLoad =
    isRoleTaxonomyVersionLoading ||
    isSkillTaxonomyVersionLoading ||
    isActivityTaxonomyVersionLoading ||
    isPreferencesLoading ||
    !isUnleashReady;
  if (isBlockingLoad && !hasMountedChildRef.current) {
    return <CenterLoading />;
  }
  hasMountedChildRef.current = true;

  return (
    <FilterProviderWithTaxonomyVersion
      {...props}
      roleTaxonomyVersion={roleTaxonomyVersion}
      skillTaxonomyVersion={skillTaxonomyVersion}
      activityTaxonomyVersion={activityTaxonomyVersion}
      activityTaxonomyEnabled={isActivityTaxonomyEnabled}
      activityFilterFlags={activityFilterFlags}
      isUnleashReady={isUnleashReady}
      onActivePrimaryFilterChange={setActivePrimaryFilterId}
      getSpecificSkillTaxonomyVersion={getSpecificSkillTaxonomyVersion}
    />
  );
};
/**
 * Provides filter state and dispatch actions to the entire application.
 * Filters contain raw user selections. Dynamic defaults are provided through useEffectiveFilters.
 */
const FilterProviderWithTaxonomyVersion = ({
  revelioPageId,
  primaryFilterId,
  defaultSelectionOverrides,
  hydrateFromUrl = true,
  syncFiltersToUrl: syncFiltersToUrlProp = true,
  persistScope = true,
  roleTaxonomyVersion,
  skillTaxonomyVersion,
  activityTaxonomyVersion,
  activityTaxonomyEnabled,
  activityFilterFlags,
  isUnleashReady,
  onActivePrimaryFilterChange,
  children,
}: FilterProviderProps & {
  roleTaxonomyVersion?: string;
  skillTaxonomyVersion?: string;
  activityTaxonomyVersion?: string;
  activityTaxonomyEnabled: boolean;
  activityFilterFlags: ActivityFilterFlags;
  isUnleashReady: boolean;
  onActivePrimaryFilterChange: (
    primaryFilterId: PrimaryFilterId | undefined
  ) => void;
  getSpecificSkillTaxonomyVersion: (
    taxonomySelection: CustomSkillTaxonomySelection
  ) => string | undefined;
}) => {
  const location = useLocation();
  const toast = useToast();
  const [hadInitialUrlFilters] = useState(() => {
    if (!hydrateFromUrl) {
      return false;
    }
    const params = new URLSearchParams(location.search);
    return params.has('filters') || params.has('filterGroups');
  });

  // Reconcile the saved/baked role defaults against the live taxonomy version.
  // Fires the info/error notice (+ Kibana log) and, when both the saved
  // preference and the baked defaults are stale, resolves the first roles from
  // the live list. It is applied via dispatch once it arrives (effect below) —
  // it can't ride the initial reducer state because the list loads async and
  // `useReducer` only reads its initial argument on mount.
  const { firstFromLiveSelections } = useRoleDefaultsStaleNotice({
    enabled: primaryFilterId === FilterId.ROLE && !hadInitialUrlFilters,
    currentRoleVersion: roleTaxonomyVersion,
  });
  const { firstFromLiveSelections: firstFromLiveSkillSelections } =
    useSkillDefaultsStaleNotice({
      enabled:
        primaryFilterId === FilterId.CUSTOM_SKILL && !hadInitialUrlFilters,
      currentSkillVersion: skillTaxonomyVersion,
    });
  const { firstFromLiveSelections: firstFromLiveActivitySelections } =
    useActivityDefaultsStaleNotice({
      enabled: primaryFilterId === FilterId.ACTIVITY && !hadInitialUrlFilters,
      currentActivityVersion: activityTaxonomyVersion,
    });

  const { filterFromUrl, urlFiltersParseFailed } = useDeserializeFilterFromUrl({
    revelioPageId,
    primaryFilterId,
    taxonomyConfig: {
      roleTaxonomyVersion,
      skillTaxonomyVersion,
      activityTaxonomyVersion,
      activityTaxonomyEnabled,
    },
    defaultSelections: defaultSelectionOverrides,
    hydrateFromUrl,
  });

  useEffect(() => {
    if (!urlFiltersParseFailed) return;
    const toastId = 'invalid-filters-from-url';
    if (toast.isActive(toastId)) return;
    toast({
      id: toastId,
      title: 'Could not load filters from the link',
      description:
        'The filters in this URL are invalid or corrupted. Default filters are being used instead.',
      status: 'warning',
      duration: 8000,
      isClosable: true,
      position: 'top-right',
    });
  }, [urlFiltersParseFailed, toast]);

  const allSelectionLists = useAllSelectionLists();
  const { selectionLists, fetching: isSelectionListsFetching } =
    useSelectionListsForKeys(standardGqlSelectionListKeys);

  const initialFilterState = (() => {
    if (filterFromUrl) return filterFromUrl;

    const shared = getSharedFilterState();
    if (persistScope && shared) return shared;

    if (!primaryFilterId) {
      return createInitialFilterConsistencyState(
        NO_PRIMARY_FILTER_KEY,
        {
          roleTaxonomyVersion,
          skillTaxonomyVersion,
          activityTaxonomyVersion,
          activityTaxonomyEnabled,
        },
        defaultSelectionOverrides
      );
    }

    return createNewFilterState({
      primaryFilterId,
      taxonomyConfig: {
        roleTaxonomyVersion,
        skillTaxonomyVersion,
        activityTaxonomyVersion,
        activityTaxonomyEnabled,
      },
      ricsK400List: selectionLists?.rics_k400,
      defaultSelections: defaultSelectionOverrides,
    });
  })();

  // Skill taxonomy versions (guaranteed loaded — the outer provider waits on
  // them) feed the adaptive-taxonomy derivation context + cold-load guard.
  const {
    isValidTaxonomyId: isValidSkillTaxonomyId,
    loading: isSkillTaxonomyVersionLoading,
  } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Skill,
  });

  const {
    isValidTaxonomyId: isValidActivityTaxonomyId,
    loading: isActivityTaxonomyVersionLoading,
  } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Activity,
    pause: !activityTaxonomyEnabled,
  });

  // Async deps the adaptive-taxonomy derivation reads. Held in a ref so the
  // reducer stays a pure function of (state, action, ctx) yet still sees data
  // that loads after mount. Activity loading is false while the flag keeps its
  // version query paused, so flag-off users never wait on it.
  const adaptiveTaxonomyCtx = useMemo<AdaptiveTaxonomyContext>(
    () => ({
      ricsK400List: (selectionLists?.rics_k400 ??
        []) as AdaptiveTaxonomyContext['ricsK400List'],
      loadingTaxonomyVersions:
        isSkillTaxonomyVersionLoading || isActivityTaxonomyVersionLoading,
      isValidSkillTaxonomyId,
      isValidActivityTaxonomyId,
      isActivityTaxonomyEnabled: activityTaxonomyEnabled,
      activityTaxonomyVersion,
    }),
    [
      selectionLists?.rics_k400,
      isSkillTaxonomyVersionLoading,
      isActivityTaxonomyVersionLoading,
      isValidSkillTaxonomyId,
      isValidActivityTaxonomyId,
      activityTaxonomyEnabled,
      activityTaxonomyVersion,
    ]
  );
  const adaptiveTaxonomyCtxRef = useRef(adaptiveTaxonomyCtx);
  adaptiveTaxonomyCtxRef.current = adaptiveTaxonomyCtx;

  const activityFilterFlagsRef = useRef({
    activityFilterFlags,
    isUnleashReady,
  });
  activityFilterFlagsRef.current = { activityFilterFlags, isUnleashReady };

  // Resolve from the destination group before enforcing membership; using the
  // previous render's gate would erase saved selections when switching groups.
  const deriveFilterState = useCallback((state: FilterConsistencyState) => {
    const { activityFilterFlags: flags, isUnleashReady: ready } =
      activityFilterFlagsRef.current;
    const context = {
      ...adaptiveTaxonomyCtxRef.current,
      isActivityTaxonomyEnabled:
        ready &&
        isActivityFilterOnForPrimary(
          flags,
          getPrimaryFilterIdFromConsistencyKey(state.activeKey)
        ),
    };
    return deriveAdaptiveTaxonomies(
      enforceActivityFilterMembership(state, context),
      context
    );
  }, []);

  // The adaptive role + skill taxonomy is derived INSIDE the reducer (see
  // `deriveAdaptiveTaxonomies`), so it is always consistent with the company in
  // the same transition that changed it — no post-render recompute, no query
  // flip — for every entry point (picker, AI apply, deeplink, comparison).
  // The activity membership invariant runs first for the same reason: every
  // write path converges on "activity filters exist iff the flag is on".
  const reducerWithAdaptiveTaxonomy = useCallback(
    (
      state: FilterConsistencyState,
      action: FilterAction
    ): FilterConsistencyState =>
      deriveFilterState(filterReducer(state, action)),
    [deriveFilterState]
  );

  const [filterState, dispatch] = useReducer(
    reducerWithAdaptiveTaxonomy,
    initialFilterState,
    deriveFilterState
  );

  const activePrimaryFilterId = getPrimaryFilterIdFromConsistencyKey(
    filterState.activeKey
  );
  useEffect(() => {
    onActivePrimaryFilterChange(activePrimaryFilterId);
  }, [activePrimaryFilterId, onActivePrimaryFilterChange]);

  // Cold load: the company can be selected before the RICS tree / taxonomy
  // versions arrive, so the initial derive can't finalize. Re-derive once they
  // load; warm changes are already handled by the reducer wrapper above.
  const adaptiveTaxonomyDepsReady =
    (selectionLists?.rics_k400?.length ?? 0) > 0 &&
    !isSkillTaxonomyVersionLoading &&
    !isActivityTaxonomyVersionLoading;
  useEffect(() => {
    if (adaptiveTaxonomyDepsReady) {
      dispatch({ type: 'REDERIVE_ADAPTIVE_TAXONOMY' });
    }
  }, [adaptiveTaxonomyDepsReady]);

  // The flag's real value can land after mount (`flagsReady || flagsError`
  // precedes it on transient unleash failures). Any dispatch runs the
  // membership enforcement above — fire one so a late flip converges without
  // waiting for user interaction.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the flag is the trigger — its change must re-run the reducer's membership enforcement
  useEffect(() => {
    dispatch({ type: 'REDERIVE_ADAPTIVE_TAXONOMY' });
  }, [activityTaxonomyEnabled]);

  // Write-through to the in-memory shared state on every change so the next
  // FilterProvider mount within the same session can hydrate from it.
  useEffect(() => {
    if (!persistScope) return;
    setSharedFilterState(filterState);
  }, [filterState, persistScope]);

  const isIsolatedPreviewInstance =
    persistScope === false && hydrateFromUrl === false;

  useSyncMetricModePostingSource({ filterState, dispatch });

  // Composition-style queries pause until the reducer has finalized the
  // adaptive taxonomy for the current company (see `deriveAdaptiveTaxonomies`).
  // Isolated previews (e.g. GA chart preview) don't load company/RICS data and
  // never use adaptive taxonomy, so they never wait.
  const isTaxonomyResolved =
    isIsolatedPreviewInstance || (filterState.taxonomyReady ?? false);

  const activeFilterMap = getActiveFilterMap(filterState, {
    roleTaxonomyVersion,
    skillTaxonomyVersion,
    activityTaxonomyVersion,
  });

  // Apply the live-list role fallback once it resolves. The initial reducer
  // state was built from the baked defaults (the list loads async), so push the
  // live roles in via the primary-selection action the chips render from —
  // UPDATE_PRIMARY_SELECTIONS reconciles the group's selections, which a plain
  // UPDATE_FILTER does not. Runs once.
  const appliedLiveRoleDefaultsRef = useRef(false);
  useEffect(() => {
    if (appliedLiveRoleDefaultsRef.current) return;
    if (!firstFromLiveSelections || firstFromLiveSelections.length === 0)
      return;
    appliedLiveRoleDefaultsRef.current = true;
    dispatch({
      type: 'UPDATE_PRIMARY_SELECTIONS',
      payload: {
        filterId: FilterId.ROLE,
        newSelections: firstFromLiveSelections,
        limit: MAX_PRIMARY_ENTITY_SELECTIONS,
      },
    });
  }, [firstFromLiveSelections]);

  // Same one-shot live fallback for skill defaults.
  const appliedLiveSkillDefaultsRef = useRef(false);
  useEffect(() => {
    if (appliedLiveSkillDefaultsRef.current) return;
    if (
      !firstFromLiveSkillSelections ||
      firstFromLiveSkillSelections.length === 0
    )
      return;
    appliedLiveSkillDefaultsRef.current = true;
    dispatch({
      type: 'UPDATE_PRIMARY_SELECTIONS',
      payload: {
        filterId: FilterId.CUSTOM_SKILL,
        newSelections: firstFromLiveSkillSelections,
        limit: MAX_PRIMARY_ENTITY_SELECTIONS,
      },
    });
  }, [firstFromLiveSkillSelections]);

  // And for activity defaults.
  const appliedLiveActivityDefaultsRef = useRef(false);
  useEffect(() => {
    if (appliedLiveActivityDefaultsRef.current) return;
    if (
      !firstFromLiveActivitySelections ||
      firstFromLiveActivitySelections.length === 0
    )
      return;
    appliedLiveActivityDefaultsRef.current = true;
    dispatch({
      type: 'UPDATE_PRIMARY_SELECTIONS',
      payload: {
        filterId: FilterId.ACTIVITY,
        newSelections: firstFromLiveActivitySelections,
        limit: MAX_PRIMARY_ENTITY_SELECTIONS,
      },
    });
  }, [firstFromLiveActivitySelections]);

  const normalizedActiveFilterMap: FilterSelectionsMap = (() => {
    const map = new Map(activeFilterMap);

    const isTreeFilter = (f: Filter | undefined): f is TreeFilter =>
      Boolean(f && f.type === FilterType.TREE);

    const customDef = getFilterDefinition(FilterId.CUSTOM_SKILL);
    if (!customDef) return map;

    const existingCustom = map.get(FilterId.CUSTOM_SKILL);
    const skill = map.get(FilterId.SKILL);

    // Always remove legacy SKILL entry when feature flag is on
    map.delete(FilterId.SKILL);

    // If CUSTOM_SKILL already has selections, keep as-is
    if (
      isTreeFilter(existingCustom) &&
      Array.isArray(existingCustom.selections) &&
      existingCustom.selections.length > 0
    ) {
      return map;
    }

    // Otherwise, if SKILL had selections, remap them into CUSTOM_SKILL
    if (
      isTreeFilter(skill) &&
      Array.isArray(skill.selections) &&
      skill.selections.length > 0
    ) {
      const selectionListIds = customDef.selectionListIds;
      const remappedSelections = skill.selections.map((sel) => {
        const depth = String(sel.id).split('_').length - 1;
        const newLevelId = selectionListIds[depth] ?? sel.levelId;
        return { ...sel, levelId: newLevelId };
      });

      const remappedCustom: TreeFilter = {
        id: FilterId.CUSTOM_SKILL,
        name: customDef.name,
        type: customDef.type,
        isVisible: customDef.isVisible,
        selectionListIds: customDef.selectionListIds,
        selections: remappedSelections,
      };

      map.set(FilterId.CUSTOM_SKILL, remappedCustom);
      return map;
    }

    // No selections to migrate; keep map (with SKILL removed)
    return map;
  })();

  // Type guards to refine filters from the selections map
  const isSingleValueFilter = <T,>(
    f: Filter | undefined,
    id: FilterId
  ): f is SingleValueFilter<T> =>
    Boolean(f && f.type === FilterType.VALUE && f.id === id);

  // A deeplink or saved view carries its author's geography taxonomy here, the
  // same way role/skill/activity travel. Falsy id means the link expressed no
  // taxonomy — see `geographyTaxonomyDescriptor`.
  const geographyTaxonomyFilter = activeFilterMap.get(
    FilterId.GEOGRAPHY_TAXONOMY
  );
  const geographyTaxonomyFromFilter =
    isSingleValueFilter<CustomGeographyTaxonomySelection>(
      geographyTaxonomyFilter,
      FilterId.GEOGRAPHY_TAXONOMY
    )
      ? geographyTaxonomyFilter.value
      : undefined;
  const geographyTaxonomyOverride = geographyTaxonomyFromFilter?.taxonomyId
    ? geographyTaxonomyFromFilter
    : undefined;

  const geographyTaxonomy = useGeographyTaxonomy({
    overrideTaxonomy: geographyTaxonomyOverride,
  });
  const usesStateBreakdownOverMsa: boolean =
    geographyTaxonomy.usesStateBreakdownOverMsa;

  // The picker follows the taxonomy's own levels. The screeners are the one
  // exception — see `COUNTY_EXCLUDED_PAGE_IDS`.
  const geographySelectionListIds = useMemo(() => {
    const levelIds = geographyTaxonomy.selectionListIds;
    return revelioPageId !== undefined &&
      COUNTY_EXCLUDED_PAGE_IDS.has(revelioPageId)
      ? levelIds.filter((levelId) => levelId !== 'county')
      : levelIds;
  }, [geographyTaxonomy.selectionListIds, revelioPageId]);

  const geographyModeRef = useRef(usesStateBreakdownOverMsa);
  geographyModeRef.current = usesStateBreakdownOverMsa;
  const geographySelectionListIdsRef = useRef(geographySelectionListIds);
  geographySelectionListIdsRef.current = geographySelectionListIds;

  const geographyAdjustedActiveFilterMap = applyGeographyTaxonomyToFilterMap(
    normalizedActiveFilterMap,
    geographySelectionListIds,
    geographyTaxonomy.branchLabels
  );

  useGeographyTaxonomyChangeEffect({
    geographySelectionListIds,
    geographyTaxonomyId: geographyTaxonomy.taxonomyId,
    normalizedActiveFilterMap,
    filterStateActiveKey: filterState.activeKey,
    dispatch,
  });

  // Resolve current numeric skill taxonomy id from active filters so selection list fetches reflect UI changes
  const skillTaxonomyFilter = activeFilterMap.get(FilterId.SKILL_TAXONOMY);
  const skillTaxonomyFilterResolved =
    isSingleValueFilter<CustomSkillTaxonomySelection>(
      skillTaxonomyFilter,
      FilterId.SKILL_TAXONOMY
    )
      ? skillTaxonomyFilter
      : undefined;
  const skillTaxonomyOverrideId = skillTaxonomyFilterResolved?.value.taxonomyId;
  const skillTaxonomyVersionOverride =
    skillTaxonomyFilterResolved?.value.version;

  const {
    customSkillTree,
    skillTaxonomyReferenceNestedTree,
    isCustomSkillTaxonomyPending,
  } = useCustomSkillTaxonomyTree(
    skillTaxonomyOverrideId,
    skillTaxonomyVersionOverride
  );

  const activityTaxonomyFilter = activeFilterMap.get(
    FilterId.ACTIVITY_TAXONOMY
  );
  const activityTaxonomyFilterResolved =
    isSingleValueFilter<CustomActivityTaxonomySelection>(
      activityTaxonomyFilter,
      FilterId.ACTIVITY_TAXONOMY
    )
      ? activityTaxonomyFilter
      : undefined;

  const { activityTree, isActivityTaxonomyPending } = useActivityTaxonomyTree({
    overrideTaxonomyId: activityTaxonomyFilterResolved?.value.taxonomyId,
    taxonomyVersionOverride: activityTaxonomyFilterResolved?.value.version,
    enabled: activityTaxonomyEnabled,
  });

  const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
  const roleTaxonomyValue = isSingleValueFilter<CustomRoleTaxonomySelection>(
    roleTaxonomyFilter,
    FilterId.ROLE_TAXONOMY
  )
    ? roleTaxonomyFilter.value
    : undefined;
  const roleReferenceTaxonomyId = useMemo(
    () => getRoleTaxonomyId(roleTaxonomyValue),
    [roleTaxonomyValue]
  );
  const {
    treeData: rawRoleReferenceTree,
    fetching: isRoleReferenceTreeFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Role,
    taxonomyId: roleReferenceTaxonomyId,
    levels: ROLE_TAXONOMY_TREE_LEVELS,
    pause: roleReferenceTaxonomyId === undefined,
  });
  const roleReferenceTree = useMemo(
    () =>
      toNestedTaxonomyTree({
        nodes: rawRoleReferenceTree,
        selectionListIds: ROLE_TAXONOMY_SELECTION_LIST_IDS,
        includeNodeMetadata: true,
        filterInvalidNodeNames: false,
      }),
    [rawRoleReferenceTree]
  );
  const {
    treeData: rawGeographyReferenceTree,
    fetching: isGeographyReferenceTreeFetching,
  } = useGeographyTaxonomyTree({
    taxonomyId: geographyTaxonomy.taxonomyId,
  });
  const geographyReferenceTree = useMemo(
    () =>
      // Prune to the level keys this page actually offers. Truncating the key
      // list alone is not enough: the tree still carries the deeper nodes, so a
      // county-incapable page would keep rendering county rows (they would just
      // have no levelId). Mirrors `ricsIndustryReferenceTree`.
      pruneTreeToDepth(
        toNestedTaxonomyTree({
          nodes: rawGeographyReferenceTree,
          selectionListIds: geographySelectionListIds,
          includeNodeMetadata: false,
          filterInvalidNodeNames: true,
        }),
        geographySelectionListIds.length - 1
      ),
    [geographySelectionListIds, rawGeographyReferenceTree]
  );
  const {
    treeData: rawRicsReferenceTree,
    fetching: isRicsReferenceTreeFetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Rics,
    taxonomyId: RICS_TAXONOMY_ID,
    levels: RICS_TAXONOMY_TREE_LEVELS,
  });
  const ricsCompanyReferenceTree = useMemo(
    () =>
      stripTreeNodeTooltipData(
        toNestedTaxonomyTree({
          nodes: rawRicsReferenceTree,
          selectionListIds: RICS_COMPANY_SELECTION_LIST_IDS,
          includeNodeMetadata: true,
          filterInvalidNodeNames: false,
        })
      ),
    [rawRicsReferenceTree]
  );
  const ricsIndustryReferenceTree = useMemo(
    () =>
      pruneTreeToDepth(
        ricsCompanyReferenceTree,
        RICS_INDUSTRY_SELECTION_LIST_IDS.length - 1
      ),
    [ricsCompanyReferenceTree]
  );

  const flatRoleLists = useMemo(
    () =>
      roleReferenceTree.length > 0
        ? flattenTreeToSelectionLists(
            roleReferenceTree,
            ROLE_TAXONOMY_SELECTION_LIST_IDS
          )
        : [],
    [roleReferenceTree]
  );

  const flatGeoLists = useMemo(
    () =>
      geographyReferenceTree.length > 0
        ? flattenTreeToSelectionLists(
            geographyReferenceTree,
            geographySelectionListIds
          )
        : [],
    [geographyReferenceTree, geographySelectionListIds]
  );

  const flatRicsLists = useMemo(
    () =>
      ricsCompanyReferenceTree.length > 0
        ? flattenTreeToSelectionLists(
            ricsCompanyReferenceTree,
            RICS_COMPANY_SELECTION_LIST_IDS
          )
        : [],
    [ricsCompanyReferenceTree]
  );

  const flatSkillLists = useMemo(
    () =>
      skillTaxonomyReferenceNestedTree &&
      skillTaxonomyReferenceNestedTree.length > 0
        ? flattenTreeToSelectionLists(
            skillTaxonomyReferenceNestedTree,
            SKILL_TAXONOMY_SELECTION_LIST_KEYS
          )
        : [],
    [skillTaxonomyReferenceNestedTree]
  );

  const flatActivityLists = useMemo(
    () =>
      activityTree && activityTree.length > 0
        ? flattenTreeToSelectionLists(
            activityTree,
            ACTIVITY_TAXONOMY_SELECTION_LIST_KEYS
          )
        : [],
    [activityTree]
  );

  // Role AND skill labels must resolve from the ACTIVE taxonomy's tree lists:
  // the preference-derived lists inside allSelectionLists can name the same
  // numeric id differently (useAllSelectionLists reads the PREFERENCE
  // taxonomy, which can disagree with this provider's SKILL_TAXONOMY filter),
  // and label lookup takes the first list with a matching id. `flatRoleLists`
  // / `flatSkillLists` are built from the filter-driven reference trees, so
  // they are the only valid label sources here. While a tree is loading,
  // expose no lists for it — a raw id self-heals once the tree lands, while a
  // wrong-taxonomy label looks valid and is never re-patched.
  const allSelectionListsWithCurrentRoleTaxonomy = useMemo(() => {
    const roleListsForLabels = isRoleReferenceTreeFetching ? [] : flatRoleLists;
    const skillListsForLabels = isCustomSkillTaxonomyPending
      ? []
      : flatSkillLists;
    const activityListsForLabels = isActivityTaxonomyPending
      ? []
      : flatActivityLists;
    return [
      // `flatGeoLists` comes FIRST because label lookup takes the first list
      // with a matching id, and these are the only geography lists built from
      // the tree this page actually renders. `useAllSelectionLists` reads the
      // PREFERENCE taxonomy, which disagrees on a page that coerces to metro.
      // Its lists stay behind these rather than being filtered out: other
      // filters (HQ location) still need `region`/`country`/`msa` whatever the
      // geography preference says.
      ...flatGeoLists,
      ...allSelectionLists.filter(
        (l) =>
          !ROLE_TAXONOMY_SELECTION_LIST_ID_SET.has(l.id) &&
          !SKILL_TAXONOMY_SELECTION_LIST_ID_SET.has(l.id) &&
          !ACTIVITY_TAXONOMY_SELECTION_LIST_ID_SET.has(l.id)
      ),
      ...roleListsForLabels,
      ...flatRicsLists,
      ...skillListsForLabels,
      ...activityListsForLabels,
    ] as typeof allSelectionLists;
  }, [
    allSelectionLists,
    isRoleReferenceTreeFetching,
    isCustomSkillTaxonomyPending,
    isActivityTaxonomyPending,
    flatRoleLists,
    flatGeoLists,
    flatRicsLists,
    flatSkillLists,
    flatActivityLists,
  ]);

  const activeFilterMapWithResolvedLabels =
    applyResolvedSelectionLabelsToFilterMap(
      geographyAdjustedActiveFilterMap,
      allSelectionListsWithCurrentRoleTaxonomy
    );

  // While any reference tree that backs a displayed filter is still fetching,
  // selection names may remain raw ids until the label patcher above can resolve
  // them. Chips use this to show a placeholder rather than the numeric id.
  const isResolvingSelectionLabels =
    isRoleReferenceTreeFetching ||
    isGeographyReferenceTreeFetching ||
    isRicsReferenceTreeFetching ||
    isCustomSkillTaxonomyPending ||
    isActivityTaxonomyPending ||
    isSelectionListsFetching;

  const entries = Array.from(activeFilterMapWithResolvedLabels.entries());

  useSyncFiltersToUrl(
    entries,
    roleTaxonomyVersion,
    skillTaxonomyVersion,
    activityTaxonomyVersion,
    revelioPageId,
    syncFiltersToUrlProp
  );

  // Retrieve processed selection lists required by a filter
  const getFilterData = useCallback(
    (filterId: FilterId): FilterDataResult => {
      const definition = getFilterDefinition(filterId);
      const handler = getFilterHandler(filterId);

      let requiredLists: (GqlSelectionListKey | RoleSelectionListKey)[] = [];

      // For geography, the level keys come from the active taxonomy. We read
      // from a ref so that the useCallback does not need them in its deps
      // (which would cause infinite re-renders via context propagation).
      if (filterId === FilterId.GEOGRAPHY) {
        requiredLists = [
          ...geographySelectionListIdsRef.current,
        ] as GqlSelectionListKey[];
      } else if (
        handler &&
        typeof handler.getRequiredSelectionListIds === 'function' &&
        definition
      ) {
        // Narrowing union generic across definition/handler is not possible here.
        // Both come from the same registry keyed by filterId, so this call is safe.
        requiredLists = (
          handler.getRequiredSelectionListIds as (
            def: typeof definition
          ) => (GqlSelectionListKey | RoleSelectionListKey)[]
        )(definition);
      }

      // Do NOT append `city` here: the derived level keys already carry the
      // taxonomy's own fourth level, and forcing `city` would put it back on
      // the state/county taxonomy, whose fourth level is county.

      if (filterId === FilterId.ROLE) {
        return isRoleReferenceTreeFetching ? [] : roleReferenceTree;
      }

      if (filterId === FilterId.GEOGRAPHY) {
        return isGeographyReferenceTreeFetching ? [] : geographyReferenceTree;
      }

      if (isRicsCompanyFilter(filterId)) {
        return isRicsReferenceTreeFetching ? [] : ricsCompanyReferenceTree;
      }

      if (filterId === FilterId.INDUSTRY) {
        return isRicsReferenceTreeFetching ? [] : ricsIndustryReferenceTree;
      }

      if (filterId === FilterId.ACTIVITY) {
        return isActivityTaxonomyPending ? [] : (activityTree ?? []);
      }

      if (isSelectionListsFetching) return [];

      if (filterId === FilterId.CUSTOM_SKILL) {
        // Hide level-4 nodes from the main filter selection UI while
        // keeping them available to consumers that call the taxonomy tree
        // directly (e.g. subfilters in charts). We prune children deeper
        // than the deepest declared selection list id for this filter.
        const def = getFilterDefinition(FilterId.CUSTOM_SKILL);
        const maxVisibleDepth = Math.max(
          0,
          (def?.selectionListIds?.length ?? 4) - 1
        );

        const pruneToDepth = (
          nodes: CustomSkillTreeNode[] | null,
          depth: number
        ): TreeData[] => {
          if (!nodes || nodes.length === 0) return [];
          return nodes.map((n) => {
            const hasChildren =
              Array.isArray(n.children) && n.children.length > 0;
            const levelId = SKILL_TAXONOMY_SELECTION_LIST_KEYS[depth];
            const baseData: TreeData = {
              id: n.id,
              name: n.name,
              levelId,
              ...(n.taxonomyTitles != null && {
                taxonomyTitles: n.taxonomyTitles,
              }),
              ...(n.suggestedTitles && { suggestedTitles: n.suggestedTitles }),
            };

            if (!hasChildren || depth >= maxVisibleDepth) {
              return baseData;
            }

            return {
              ...baseData,
              children: pruneToDepth(
                n.children as CustomSkillTreeNode[],
                depth + 1
              ),
            };
          });
        };

        return pruneToDepth(customSkillTree, 0);
      }

      const gqlKeys = requiredLists.filter((k): k is GqlSelectionListKey =>
        gqlSelectionListKeys.includes(k as GqlSelectionListKey)
      );

      if (isMultiGranularityFilter(filterId)) {
        const cachedTree = selectionListCache.getNestedTree(
          filterId,
          requiredLists,
          selectionLists,
          null
        );
        return cachedTree ?? [];
      }

      const relevantLists = gqlKeys
        .map((listId) => {
          const list = selectionLists[listId] ?? [];
          const sortedList =
            listId === 'city' ? sortCitySelectionList(list) : list;
          return {
            selectionListId: listId,
            selectionList: sortedList,
          };
        })
        .filter((list) => list.selectionList !== undefined);

      return relevantLists as FilterDataResult;
    },
    [
      selectionLists,
      isSelectionListsFetching,
      customSkillTree,
      activityTree,
      isActivityTaxonomyPending,
      roleReferenceTree,
      isRoleReferenceTreeFetching,
      geographyReferenceTree,
      isGeographyReferenceTreeFetching,
      ricsCompanyReferenceTree,
      ricsIndustryReferenceTree,
      isRicsReferenceTreeFetching,
    ]
  );

  return (
    <FilterContext.Provider
      value={{
        filterState,
        activeFilterMap: activeFilterMapWithResolvedLabels,
        dispatch,
        getFilterData,
        hadInitialUrlFilters,
        defaultSelectionOverrides,
        isTaxonomyResolved,
        isResolvingSelectionLabels,
        usesStateBreakdownOverMsa,
        geographySelectionListIds,
        geographyTaxonomyId: geographyTaxonomy.taxonomyId,
        geographyTaxonomyVersion: geographyTaxonomy.version,
        activityTaxonomyEnabled,
        activityTaxonomyVersion,
        skillTaxonomyId: skillTaxonomyOverrideId,
        skillTaxonomyVersion: skillTaxonomyVersionOverride,
        activityTaxonomyId: activityTaxonomyFilterResolved?.value.taxonomyId,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilterContext must be used within a FilterProvider');
  }
  return context;
};

/**
 * Returns true once adaptive taxonomy hooks have resolved for company pages.
 * Always true for non-company pages. Use to `pause` URQL queries until
 * the correct taxonomy is known.
 */
export const useIsTaxonomyResolved = (): boolean => {
  const { isTaxonomyResolved } = useFilterContext();
  return isTaxonomyResolved;
};

/**
 * Custom hook to get a function for removing a filter (reverting to its default state).
 */
export const useRemoveFilter = () => {
  const { dispatch } = useFilterContext();
  return (filterId: FilterId) => dispatch({ type: 'REMOVE_FILTER', filterId });
};

/**
 * A hook to fetch and manage the specific data required for a single filter component.
 */
export const useSingleFilterData = (filterId: FilterId) => {
  const { getFilterData, usesStateBreakdownOverMsa } = useFilterContext();
  const [data, setData] = useState<FilterDataResult | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const rawData = getFilterData(filterId);
    setData(rawData?.length ? rawData : null);
    // usesStateBreakdownOverMsa is included so that geography filters re-fetch tree data
    // when the mode changes, even though getFilterData reads mode via a ref.
  }, [filterId, getFilterData, usesStateBreakdownOverMsa]);

  return data;
};

/**
 * Checks if a filter uses multiple selection lists (e.g., GEO with country, state, city).
 */
const isMultiGranularityFilter = <T extends FilterId>(filterId: T): boolean => {
  const definition = getFilterDefinition(filterId);
  const handler = getFilterHandler(filterId);
  let requiredLists: AllSelectionListKeys[] = [];
  if (
    handler &&
    typeof handler.getRequiredSelectionListIds === 'function' &&
    definition
  ) {
    requiredLists = (
      handler.getRequiredSelectionListIds as (
        def: typeof definition
      ) => AllSelectionListKeys[]
    )(definition);
  }
  return requiredLists.length > 1;
};

//
